/* eslint-disable vue/one-component-per-file */
import Vue, { ref } from "vue";
import { i18n } from "@/plugins/commonImports";
import vuetify from "@/plugins/vuetify";
import type { ComponentPublicInstanceConstructor } from "vue/types/v3-component-public-instance";
import { errorCaptured } from "@/plugins/vueErrorHandler";

type ComponentProps<D extends ComponentPublicInstanceConstructor> = Omit<
  InstanceType<D>["$props"],
  "value"
>;

export interface DialogResult<T> {
  wasSuccessful: boolean;
  value: T | null;
}

/**
 * Mounts and displays a dialog - can be executed from any async handler.
 */
export function showModalDialog<
  T,
  D extends ComponentPublicInstanceConstructor,
>(
  dialog: ComponentPublicInstanceConstructor,
  options: {
    props: ComponentProps<D>;
    attrs?: Record<string, any>;
  }
): Promise<DialogResult<T>> {
  const { props: propsData, attrs } = options;
  const showDialog = ref(true);

  const props: Record<string, any> = {
    value: showDialog.value,
    visible: showDialog.value,
    ...propsData,
  };

  // Wrap our dialog component into a shim so we can correctly pass in props/attrs
  const shimCtor = Vue.extend({
    render: (h) =>
      h(dialog as any, {
        props,
        attrs,
      }),
  });

  // Bind in our vuetify, i18n and error capturing behaviours
  const shimComp = new shimCtor({
    vuetify,
    i18n,
    errorCaptured,
  });

  const mountEl = document.createElement("div");
  document.getElementsByClassName("v-application")[0].appendChild(mountEl);
  shimComp.$mount(mountEl);

  function close() {
    showDialog.value = false;
    shimComp.$destroy();
    shimComp.$el.remove();
  }

  return new Promise<DialogResult<T>>((resolve, _) => {
    // Listen to first child events as we have now wrapped our dialog in a shim
    shimComp.$children[0].$on("accept", async (value: any) => {
      close();
      resolve({
        wasSuccessful: true,
        value: value,
      });
    });

    shimComp.$children[0].$on("cancel", () => {
      close();
      resolve({
        wasSuccessful: false,
        value: null,
      });
    });
  });
}
